import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Heart, Search, X, CheckCircle, UserPlus, Check, XCircle, Loader2, Gamepad2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Game info mapping
const GAME_INFO = {
  'get-to-know-you': { name: 'Get to Know You', emoji: '💬', color: 'from-blue-500 to-cyan-500' },
  'love-psychology-quiz': { name: 'Love & Psychology Quiz', emoji: '💕', color: 'from-pink-500 to-rose-500' },
  'compatibility-challenge': { name: 'Compatibility Challenge', emoji: '🧠', color: 'from-purple-500 to-indigo-500' },
  'ping-pong': { name: 'Ping Pong Game', emoji: '🏓', color: 'from-green-500 to-emerald-500' }
};

const Matches = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const inviteGame = searchParams.get('invite');
  const gameInfo = inviteGame ? GAME_INFO[inviteGame] : null;
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'matches');
  const [matches, setMatches] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [processingRequest, setProcessingRequest] = useState(null);

  useEffect(() => {
    loadMatches();
    loadRequests();
  }, []);

  useEffect(() => {
    // Update URL when tab changes
    setSearchParams(activeTab === 'matches' ? {} : { tab: activeTab });
  }, [activeTab]);

  useEffect(() => {
    // Filter matches based on search query
    if (searchQuery.trim() === '') {
      setFilteredMatches(matches);
    } else {
      const filtered = matches.filter(match =>
        match.user.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredMatches(filtered);
    }
  }, [searchQuery, matches]);

  const loadMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches`, { withCredentials: true });
      setMatches(response.data);
      setFilteredMatches(response.data);
      setLoading(false);
    } catch (e) {
      console.error('Failed to load matches:', e);
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await axios.get(`${API}/friend-requests`, { withCredentials: true });
      setRequests(response.data.requests || []);
    } catch (e) {
      console.error('Failed to load requests:', e);
    } finally {
      setLoadingRequests(false);
    }
  };

  const acceptRequest = async (requestId) => {
    try {
      setProcessingRequest(requestId);
      const response = await axios.post(`${API}/friend-requests/${requestId}/accept`, {}, { withCredentials: true });
      toast.success("It's a Match! 🎉 You can now message each other.");
      
      // Remove from requests and reload matches
      setRequests(prev => prev.filter(r => r.id !== requestId));
      loadMatches();
      
      // Switch to matches tab
      setActiveTab('matches');
    } catch (e) {
      toast.error('Failed to accept request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const declineRequest = async (requestId) => {
    try {
      setProcessingRequest(requestId);
      await axios.post(`${API}/friend-requests/${requestId}/decline`, {}, { withCredentials: true });
      setRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success('Request declined');
    } catch (e) {
      toast.error('Failed to decline request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center space-x-4">
          <Button
            data-testid="back-btn"
            onClick={() => inviteGame ? navigate('/games') : navigate('/discover')}
            variant="ghost"
            size="icon"
            className="hover:bg-pink-100 rounded-full"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </Button>
          {inviteGame && gameInfo ? (
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
                {gameInfo.emoji} Invite to Play
              </h1>
              <p className="text-sm text-gray-600">Select a match to send {gameInfo.name} invite</p>
            </div>
          ) : (
            <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
              Connections
            </h1>
          )}
        </div>
        
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 pb-2">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('matches')}
              className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                activeTab === 'matches'
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                  : 'bg-white/50 text-gray-600 hover:bg-white/80'
              }`}
            >
              <MessageCircle className="w-4 h-4 inline mr-2" />
              Matches ({matches.length})
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all relative ${
                activeTab === 'requests'
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                  : 'bg-white/50 text-gray-600 hover:bg-white/80'
              }`}
            >
              <UserPlus className="w-4 h-4 inline mr-2" />
              Requests
              {requests.length > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === 'requests' ? 'bg-white text-pink-600' : 'bg-pink-500 text-white'
                }`}>
                  {requests.length}
                </span>
              )}
            </button>
          </div>
        </div>
        
        {/* Search Bar - only for matches tab */}
        {activeTab === 'matches' && matches.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 pb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search matches by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 py-6 rounded-full border-2 border-purple-200 focus:border-purple-400 text-base"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Matches Tab */}
        {activeTab === 'matches' && (
          <>
            {matches.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-10 h-10 text-pink-400" />
                </div>
                <h2 className="text-2xl font-bold text-gray-700 mb-2">No Matches Yet</h2>
                <p className="text-gray-500 mb-6">Start swiping to find your perfect match!</p>
                <Button
                  onClick={() => navigate('/discover')}
                  data-testid="start-swiping-btn"
                  className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-full px-8"
                >
                  Start Swiping
                </Button>
              </div>
            ) : filteredMatches.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-10 h-10 text-purple-400" />
                </div>
                <h2 className="text-2xl font-bold text-gray-700 mb-2">No matches found</h2>
                <p className="text-gray-500 mb-6">Try searching with a different name</p>
                <Button
                  onClick={clearSearch}
                  variant="outline"
                  className="border-purple-300 text-purple-600 hover:bg-purple-50 rounded-full px-8"
                >
                  Clear Search
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredMatches.map((match) => (
                  <div
                    key={match.match_id}
                    data-testid={`match-card-${match.match_id}`}
                    className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    style={{transition: 'box-shadow 0.3s ease, transform 0.3s ease'}}
                  >
                    <div className="flex items-center space-x-4">
                      <div 
                        onClick={() => navigate(`/profile/${match.user.id}`)}
                        className="cursor-pointer"
                      >
                        <Avatar className="w-16 h-16">
                          <AvatarImage src={match.user.photos?.[0]?.startsWith('http') ? match.user.photos[0] : (match.user.photos?.[0] ? `${BACKEND_URL}${match.user.photos[0]}` : match.user.picture)} />
                          <AvatarFallback className="bg-gradient-to-br from-pink-400 to-purple-500 text-white text-xl">
                            {match.user.name[0]}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <h3 
                              className="text-lg font-bold text-gray-800 truncate cursor-pointer hover:text-pink-600"
                              onClick={() => navigate(`/profile/${match.user.id}`)}
                            >
                              {match.user.name}
                            </h3>
                            {match.user.verification_status === 'approved' && (
                              <div className="flex items-center space-x-1 bg-blue-100 px-2 py-0.5 rounded-full flex-shrink-0">
                                <CheckCircle className="w-3 h-3 text-blue-600" />
                                <span className="text-xs font-semibold text-blue-700">Verified</span>
                              </div>
                            )}
                          </div>
                          {match.last_message_at ? (
                            <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                              {formatDistanceToNow(new Date(match.last_message_at), { addSuffix: true })}
                            </span>
                          ) : match.matched_at && (
                            <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                              {formatDistanceToNow(new Date(match.matched_at), { addSuffix: true })}
                            </span>
                          )}
                        </div>
                        {match.last_message ? (
                          <p className="text-sm text-gray-600 truncate">{match.last_message}</p>
                        ) : (
                          <p className="text-sm text-purple-500 font-medium">Say hi! 👋</p>
                        )}
                      </div>
                    </div>

                    {/* Message / Game Invite Button */}
                    {inviteGame && gameInfo ? (
                      <Button
                        data-testid={`invite-btn-${match.match_id}`}
                        onClick={() => navigate(`/chat/${match.match_id}?game=${inviteGame}`)}
                        className={`w-full mt-3 bg-gradient-to-r ${gameInfo.color} hover:opacity-90 text-white rounded-full py-3`}
                      >
                        <Gamepad2 className="w-4 h-4 mr-2" />
                        Send {gameInfo.name} Invite
                      </Button>
                    ) : (
                      <Button
                        data-testid={`message-btn-${match.match_id}`}
                        onClick={() => navigate(`/chat/${match.match_id}`)}
                        className="w-full mt-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-full py-3"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Send Message
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <>
            {loadingRequests ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="w-10 h-10 text-purple-400" />
                </div>
                <h2 className="text-2xl font-bold text-gray-700 mb-2">No Pending Requests</h2>
                <p className="text-gray-500 mb-6">When someone likes you, their request will appear here</p>
                <Button
                  onClick={() => navigate('/discover')}
                  className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-full px-8"
                >
                  Discover People
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-500 text-center mb-4">
                  Accept requests to start chatting
                </p>
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <div 
                        onClick={() => navigate(`/profile/${request.from_user.id}`)}
                        className="cursor-pointer"
                      >
                        <Avatar className="w-16 h-16">
                          <AvatarImage src={request.from_user.photos?.[0]?.startsWith('http') ? request.from_user.photos[0] : (request.from_user.photos?.[0] ? `${BACKEND_URL}${request.from_user.photos[0]}` : request.from_user.picture)} />
                          <AvatarFallback className="bg-gradient-to-br from-pink-400 to-purple-500 text-white text-xl">
                            {request.from_user.name?.[0] || '?'}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 
                          className="text-lg font-bold text-gray-800 truncate cursor-pointer hover:text-pink-600"
                          onClick={() => navigate(`/profile/${request.from_user.id}`)}
                        >
                          {request.from_user.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {request.from_user.age && `${request.from_user.age} years`}
                          {request.from_user.city && ` • ${request.from_user.city}`}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>

                    {request.from_user.bio && (
                      <p className="text-sm text-gray-600 mt-3 line-clamp-2">{request.from_user.bio}</p>
                    )}

                    {/* Accept/Decline Buttons */}
                    <div className="flex space-x-3 mt-4">
                      <Button
                        onClick={() => declineRequest(request.id)}
                        disabled={processingRequest === request.id}
                        variant="outline"
                        className="flex-1 border-gray-300 text-gray-600 hover:bg-gray-50 rounded-full"
                      >
                        {processingRequest === request.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 mr-2" />
                            Decline
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => acceptRequest(request.id)}
                        disabled={processingRequest === request.id}
                        className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-full"
                      >
                        {processingRequest === request.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Accept
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Matches;
